import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import type z from 'zod';

import { RadioGroup } from '@nesso/ui/primitives/radio-group';

import {
    noteFormatStepSchema,
    type StepProps,
    useUpdateOnboardingStepOptions,
} from '@/entities/practice-member';
import {
    notesSettingsOptions,
    useUpdateNotesSettingsOptions,
} from '@/entities/practice-member-settings';
import { BulletList } from '@/shared/ui/forms/bullet-list';
import { CardFormSection } from '@/shared/ui/forms/card-form-section';
import { RadioCardItem } from '@/shared/ui/forms/radio-card-item';

export function OnboardingNoteFormatStep(props: StepProps) {
    const { data: notesSettings } = useSuspenseQuery(notesSettingsOptions());

    const form = useForm<z.infer<typeof noteFormatStepSchema>>({
        mode: 'onChange',
        resolver: zodResolver(noteFormatStepSchema),
        defaultValues: { noteFormat: notesSettings.value.noteFormat },
    });
    const {
        formState: { isDirty, isSubmitting, isSubmitted, isValid },
    } = form;

    const mutation = useMutation(useUpdateNotesSettingsOptions());
    const updateOnboardingStepMutation = useMutation(useUpdateOnboardingStepOptions());

    const onSubmit = async (data: z.infer<typeof noteFormatStepSchema>) => {
        try {
            if (props.nextStep) {
                updateOnboardingStepMutation.mutate(props.nextStep);
            }
            if (isDirty) {
                await mutation.mutateAsync(data);
            }
            props.onNext();
        } catch {
            console.error('Failed to update note format step');
        }
    };

    const handleManualSubmit = () => {
        form.handleSubmit(onSubmit)();
    };

    return (
        <FormProvider {...form}>
            <CardFormSection
                title="Time to personalize your notes"
                description="Choose the format you prefer"
                progressValue={props.progress}
                isError={isSubmitted && !isValid}
                isFatalError={mutation.isError}
                disabledBackButton={isSubmitting}
                disabledNextButton={isSubmitting}
                onNext={handleManualSubmit}
                onBack={props.onBack}
            >
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Controller
                        control={form.control}
                        name="noteFormat"
                        render={({ field }) => (
                            <RadioGroup
                                {...field}
                                onValueChange={field.onChange}
                                disabled={isSubmitting}
                                className="flex flex-col lg:flex-row gap-4 lg:gap-8 xl:gap-11 justify-center items-stretch lg:items-start"
                            >
                                <RadioCardItem
                                    value="BULLETED"
                                    title="Bulleted"
                                    description={
                                        <BulletList
                                            items={[
                                                'Reported feeling "overwhelmed" by workload and a regression in managing intrusive thoughts.',
                                                'Struggling to consistently use the "worry time" technique',
                                                'Reported sleeping only 5-6 hours on three nights; links this to increased morning anxiety.',
                                                'Reported a tense interaction with a coworker that left them feeling "defensive and frustrated."',
                                            ]}
                                        />
                                    }
                                />
                                <RadioCardItem
                                    value="NARRATIVE"
                                    title="Narrative"
                                    description={
                                        'Client reported feeling "overwhelmed" by their workload this week and noted a regression in their ability to manage intrusive thoughts. He stated he is still struggling to consistently implement the 15-minute "worry time" technique. He reported sleeping only 5-6 hours on three nights this week, which they link to increased morning anxiety. The client also reported a tense interaction with a coworker earlier in the week, which left them feeling "defensive and frustrated" for the rest of the day.'
                                    }
                                />
                            </RadioGroup>
                        )}
                    />
                </form>
            </CardFormSection>
        </FormProvider>
    );
}
